import { useEffect, useRef, useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { Autocomplete, TextField } from "@mui/material";

import { IOption } from "../../../../../../../../../shared/contracts/IReportDataOptions";
import IPivotChildColumn from "../../../../../../../../../shared/contracts/IPivotChildColumn";
import useWizardReportDataContext from "../../../../../../contexts/useWizardReportData.context";
import useWizardReportPivotService from "../../wizard-report-pivot.service";


/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Jan/05/2025
 */
export default function ChildGridFormatRenderer(
    props: ICellRendererParams<IPivotChildColumn>
) {

    const { availableColumns, pivotData, formatOptions } = useWizardReportDataContext();
    const { getFormatOptionsByDataType } = useWizardReportPivotService();

    const [options, setOptions] = useState<IOption[]>([]);
    const [selectedItem, setSelectedItem] = useState<IOption | null>(null);
    const [disabled, setDisabled] = useState(true);

    useEffect(() => {
        const data = props.node.data;
        if (formatOptions && data && data.childColumnFormatValue
            && selectedItem?.optionValue !== data.childColumnFormatValue
        ) {
            const option = formatOptions.options.find(x => x.optionValue === data.childColumnFormatValue);
            if (option) {
                setSelectedItem(option)
            }
        }
    }, [props, pivotData?.childColumns])

    useEffect(() => {
        const isInvalid = !props.data?.childColumnName;

        // Enable/Disable
        setDisabled(isInvalid);

        if (isInvalid) {
            setSelectedItem(null); // clear existing value
        } else { // isValid
            const selectedColumn = availableColumns.find((item) =>
                item.columnName === props.data?.childColumnName
            );
            if (selectedColumn) {
                const options = getFormatOptionsByDataType(selectedColumn.columnDataType);

                if (selectedItem && !options.find(x => x.optionValue === selectedItem?.optionValue)) {
                    setSelectedItem(null);
                }

                setOptions(options);
            }
        }

    }, [props.data?.childColumnName]);

    const handleSelectionChange = (selEvent: any, selOption: IOption | null) => {

        setSelectedItem(selOption);

        // If necessary, update the data object in AG Grid
        if (props.node.data) {
            props.node.setDataValue('childColumnFormatValue', selOption?.optionValue ?? null);
            props.api.refreshCells({
                rowNodes: [props.node],
                columns: ['childColumnFormatValue']
            });

            // Manually trigger onRowDataUpdated
            props.api.dispatchEvent({ type: 'rowDataUpdated' });
        }
    };



    return (<Autocomplete fullWidth size='small'
        disabled={disabled}
        options={options}
        value={selectedItem}
        onChange={handleSelectionChange}

        getOptionLabel={(option) => option?.optionName ?? ''}

        renderInput={(params) =>
            <TextField {...params}
                disabled={disabled}
                placeholder='select format...'
                sx={{
                    '& .MuiOutlinedInput-root': {
                        fontFamily: 'inherit', // Inherit font family from AG Grid
                        fontSize: 'inherit',   // Inherit font size from AG Grid
                        color: 'inherit',      // Inherit text color
                        '& fieldset': {
                            border: 'none',    // Remove border
                        },
                    },
                    '& .MuiInputBase-input': {
                        fontFamily: 'inherit', // Inherit font family
                        fontSize: 'inherit',   // Inherit font size
                        color: 'inherit',      // Inherit text color
                    },
                }}
            />}

        renderGroup={(params) => (
            <li key={params.key}>
                <ul style={{ padding: 0 }}>{params.children}</ul>
            </li>
        )}
    />);
}